import Typography from '@material-ui/core/Typography';
import classnames from 'classnames';
import Grid from '@material-ui/core/Grid';
import React, { useCallback } from 'react';
import propTypes from 'prop-types';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from 'assets/images/edit-icon.svg';

import { useWorklogStyles, useCrewBlockStyles } from '../../hooks';
import { useCallbackValuesHasChanged } from 'components/WorklogDetails/hooks/useValuesHasChanged';
import { useGetRemovedHistoryItem } from 'components/WorklogDetails/hooks/useGetRemovedHistoryItem';
import { useArrayHistoryChanges } from 'components/WorklogDetails/hooks/useArrayHistoryChanges';

const WeatherBlock = ({ weather, hasAccess, openAdditionalDrawer, historyState }) => {
  const worklogClasses = useWorklogStyles();
  const crewBlockClasses = useCrewBlockStyles();

  const isWeatherData = (weather) => {
    if (!weather) return false;
    const isAnyData =
      (weather.temperature && weather.temperature.length) || weather.humidity || weather.notes;
    return !!isAnyData;
  };

  const weatherDiff = historyState?.diff?.weather;
  const {
    removedItems: removedTemperature,
    addedItems: addedTemperature,
  } = useArrayHistoryChanges(weatherDiff?.temperature);
  const isWeatherCompletelyChanged = (Array.isArray(weatherDiff) && weatherDiff?.length);

  const { removed: isHumidityRemoved, value: prevHumidity } = useGetRemovedHistoryItem(weatherDiff?.humidity);
  const { removed: isNotesRemoved, value: prevNotes } = useGetRemovedHistoryItem(weatherDiff?.notes);
  const isHumidityChanged = !!historyState && (weatherDiff?.humidity?.length || isWeatherCompletelyChanged);
  const isNotesChanged = !!historyState && (weatherDiff?.notes?.length || isWeatherCompletelyChanged);

  const formatWeatherToString = (weather) => {
    const { temperature, humidity } = weather;
    return (
      <>
        {Boolean(temperature && temperature.length) &&
          temperature.map((t, i, arr) => (
            <span key={Math.floor(+new Date() + Math.random() * 0xffffffff).toString(36)}>
              <span className={classnames(worklogClasses.secondaryText, worklogClasses.zeroMargin)}>
                Temp -{' '}
              </span>
              <span className={classnames((addedTemperature?.includes(t) || isWeatherCompletelyChanged) ? worklogClasses.showDiff : null)}>
                {t + String('\xB0') + ' F '}
              </span>
              {humidity || i + 1 || removedTemperature?.length !== arr.length ? ' \u2022 ' : ''}
            </span>
          ))}
        {(historyState && Boolean(removedTemperature?.length)) && (
          removedTemperature.map((t, i, arr) => (
            <span
              key={Math.floor(+new Date() + Math.random() * 0xffffffff).toString(36)}
              className={classnames(worklogClasses.showDiffRemoved)}
            >
              <span className={classnames(worklogClasses.secondaryText, worklogClasses.zeroMargin)}>
                Temp -{' '}
              </span>
              {t + String('\xB0') + ' F '}
              {humidity || i + 1 !== arr.length ? ' \u2022 ' : ''}
            </span>
          ))
        )}
        {(!!humidity || !!prevHumidity) && (
          <span
            className={classnames(
              (historyState && isHumidityRemoved) ? worklogClasses.showDiffRemoved : null)
            }
          >
            <span
              className={classnames(
                worklogClasses.secondaryText,
                worklogClasses.zeroMargin,
              )}
            >
              Hum -{' '}
            </span>
            <span
              className={classnames(
                (historyState && isHumidityChanged && humidity) ? worklogClasses.showDiff : null,
              )}
            >
              {(humidity || prevHumidity) + '%'}
            </span>
          </span>
        )}
      </>
    );
  };

  const editButton = hasAccess && (
    <IconButton
      className={crewBlockClasses.editButton}
      onClick={openAdditionalDrawer('weather')}
      disableRipple
      disableTouchRipple
    >
      <img src={EditIcon} alt="edit crew" />
    </IconButton>
  );

  return (
    <>
      <Grid item xs={12} md={6}>
        <div className={crewBlockClasses.header}>
          <Typography
            variant="body1"
            color="textSecondary"
            className={classnames(worklogClasses.regularFontWeight, worklogClasses.paddingBottom)}
          >
            Weather:
          </Typography>
          <span className={worklogClasses.hideOnDesktop}>{editButton}</span>
        </div>
        <Typography variant="body1" color="textPrimary">
          {isWeatherData(weather)
            ? formatWeatherToString(weather)
            : <span className={classnames(historyState?.diff?.weather ? worklogClasses.showDiff : null)}>N/A</span>
          }
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <div className={crewBlockClasses.header}>
          <Typography
            variant="body1"
            color="textSecondary"
            className={classnames(worklogClasses.regularFontWeight, worklogClasses.paddingBottom)}
          >
            Weather Notes:
          </Typography>
          <span className={worklogClasses.hideOnMobile}>{editButton}</span>
        </div>
        <Typography variant="body1" color="textPrimary">
          <span
            className={classnames(
              (historyState && isNotesRemoved) ? worklogClasses.showDiffRemoved : null,
              (historyState && isNotesChanged && weather?.notes) ? worklogClasses?.showDiff : null,
            )}
          >
            {weather?.notes || prevNotes || 'N/A'}
          </span>
        </Typography>
      </Grid>
    </>
  );
};

WeatherBlock.propTypes = {
  weather: propTypes.object,
  hasAccess: propTypes.bool.isRequired,
  openAdditionalDrawer: propTypes.func.isRequired,
};

export default WeatherBlock;
